import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShieldCheck, Key, Users, Building2, Pencil, Trash2, Lock, Globe, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['super_admin', 'admin', 'agent'];
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', agent: 'Agent' };
const ROLE_COLORS = {
  super_admin: 'bg-[#FFB800]/10 text-[#FFB800]',
  admin: 'bg-[#007AFF]/10 text-[#007AFF]',
  agent: 'bg-[#808080]/10 text-[#808080]'
};

export default function AdminPage() {
  const { user, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [editingResend, setEditingResend] = useState(null);
  const [resendForm, setResendForm] = useState({ resend_api_key: '', sender_email: '' });
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [changingOwnPassword, setChangingOwnPassword] = useState(false);
  const [ownPasswordForm, setOwnPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [editingBusinessAccess, setEditingBusinessAccess] = useState(null);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'agent', business_ids: [] });

  const fetchData = useCallback(async () => {
    try {
      const [uRes, bRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/businesses`, { withCredentials: true }),
      ]);
      setUsers(uRes.data);
      setBusinesses(bRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateRole = async () => {
    if (!editingRole || !newRole) return;
    try {
      await axios.put(`${API}/users/${editingRole}/role`, { role: newRole }, { withCredentials: true });
      toast.success('Role updated');
      setEditingRole(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleUpdateResend = async () => {
    if (!editingResend) return;
    try {
      await axios.put(`${API}/admin/businesses/${editingResend}/resend`, resendForm, { withCredentials: true });
      toast.success('Resend config updated');
      setEditingResend(null);
      setResendForm({ resend_api_key: '', sender_email: '' });
      fetchData();
    } catch (e) { toast.error('Failed to update'); }
  };

  const openResendEdit = (b) => {
    setResendForm({ resend_api_key: b.resend_api_key || '', sender_email: b.sender_email || '' });
    setEditingResend(b.business_id);
  };

  const handleAdminResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    if (newPassword.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    try {
      await axios.put(`${API}/users/${resetPasswordUser.user_id}/password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(`Password reset for ${resetPasswordUser.name || resetPasswordUser.email}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };

  const handleChangeOwnPassword = async () => {
    if (ownPasswordForm.newPw.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    if (ownPasswordForm.newPw !== ownPasswordForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: ownPasswordForm.current,
        new_password: ownPasswordForm.newPw
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setChangingOwnPassword(false);
      setOwnPasswordForm({ current: '', newPw: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
  };

  const openBusinessAccess = (u) => {
    setEditingBusinessAccess(u);
    setSelectedBusinessIds(u.business_ids || []);
  };

  const toggleBusiness = (bizId) => {
    setSelectedBusinessIds(prev =>
      prev.includes(bizId) ? prev.filter(id => id !== bizId) : [...prev, bizId]
    );
  };

  const handleSaveBusinessAccess = async () => {
    if (!editingBusinessAccess) return;
    try {
      await axios.put(`${API}/users/${editingBusinessAccess.user_id}/businesses`, { business_ids: selectedBusinessIds }, { withCredentials: true });
      toast.success('Business access updated');
      setEditingBusinessAccess(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update business access'); }
  };

  const toggleNewUserBusiness = (bizId) => {
    setNewUserForm(prev => ({
      ...prev,
      business_ids: prev.business_ids.includes(bizId)
        ? prev.business_ids.filter(id => id !== bizId)
        : [...prev.business_ids, bizId]
    }));
  };

  const handleCreateUser = async () => {
    const { name, email, password } = newUserForm;
    if (!name || !email || !password) { toast.error('Name, email and password are required'); return; }
    if (password.length < 12) { toast.error('Password must be at least 12 characters'); return; }
    try {
      await axios.post(`${API}/users`, newUserForm, { withCredentials: true });
      toast.success(`User ${email} created`);
      setCreatingUser(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'agent', business_ids: [] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
  };

  return (
    <div className="flex-1 p-8" data-testid="admin-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
          <ShieldCheck size={28} className="inline mr-2 text-[#00FF99]" />
          Admin Panel
        </h1>
        <p className="text-[#B0B0B0] text-sm mt-1">Manage users, roles, and API configurations</p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="bg-[#0A0A0A] border border-[rgba(255,255,255,0.12)]">
          <TabsTrigger value="users" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#00FF99] text-[#B0B0B0]">
            <Users size={14} className="mr-1" /> Users & Roles
          </TabsTrigger>
          <TabsTrigger value="resend" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#00FF99] text-[#B0B0B0]">
            <Key size={14} className="mr-1" /> Resend API Keys
          </TabsTrigger>
          <TabsTrigger value="access" className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#00FF99] text-[#B0B0B0]">
            <Globe size={14} className="mr-1" /> Business Access
          </TabsTrigger>
        </TabsList>

        {/* Users & Roles Tab */}
        <TabsContent value="users" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm text-[#808080] uppercase tracking-widest">Team Members</h3>
            <div className="flex gap-2">
              {hasRole('super_admin') && (
                <Button size="sm" onClick={() => setCreatingUser(true)} data-testid="create-user-btn"
                  className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] text-xs">
                  <UserPlus size={12} className="mr-1" /> Create User
                </Button>
              )}
              <Button size="sm" onClick={() => setChangingOwnPassword(true)} data-testid="change-own-password-btn"
                className="bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99] text-xs">
                <Lock size={12} className="mr-1" /> Change My Password
              </Button>
            </div>
          </div>
          <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[rgba(255,255,255,0.12)] hover:bg-transparent">
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Name</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Email</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Role</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">2FA</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.user_id} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[#1A1A1A]">
                    <TableCell className="text-white font-medium">{u.name}</TableCell>
                    <TableCell className="text-[#B0B0B0] text-sm">{u.email}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${ROLE_COLORS[u.role] || ROLE_COLORS.agent}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs ${u.totp_enabled ? 'text-[#00FF99]' : 'text-[#808080]'}`}>
                        {u.totp_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {hasRole('super_admin') && u.user_id !== user?.user_id && (
                        <div className="flex gap-1">
                          <button onClick={() => { setEditingRole(u.user_id); setNewRole(u.role); }}
                            className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-white transition-colors duration-200"
                            data-testid={`edit-role-${u.user_id}`} title="Change role">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => { setResetPasswordUser(u); setNewPassword(''); }}
                            className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#FFB800] transition-colors duration-200"
                            data-testid={`reset-pw-${u.user_id}`} title="Reset password">
                            <Lock size={14} />
                          </button>
                          <button onClick={() => handleDeleteUser(u.user_id)}
                            className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#FF3B30] transition-colors duration-200"
                            data-testid={`delete-user-${u.user_id}`} title="Delete user">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Resend API Keys Tab */}
        <TabsContent value="resend" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.map(b => (
              <div key={b.business_id} className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: `${b.color || '#00FF99'}20` }}>
                    <Building2 size={20} style={{ color: b.color || '#00FF99' }} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{b.name}</h3>
                    <p className="text-xs text-[#808080]">{b.type}</p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#808080]">API Key</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${b.resend_api_key ? 'bg-[#00FF99]/10 text-[#00FF99]' : 'bg-[#FFB800]/10 text-[#FFB800]'}`}>
                      {b.resend_api_key ? 'Configured' : 'Not set'}
                    </span>
                  </div>
                  {b.sender_email && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#808080]">Sender</span>
                      <span className="text-xs text-[#B0B0B0]">{b.sender_email}</span>
                    </div>
                  )}
                </div>
                <Button size="sm" onClick={() => openResendEdit(b)} data-testid={`edit-resend-${b.business_id}`}
                  className="w-full bg-transparent border border-[rgba(255,255,255,0.12)] text-white hover:border-[#00FF99] hover:text-[#00FF99] text-xs">
                  <Key size={12} className="mr-1" /> Configure Resend
                </Button>
              </div>
            ))}
            {businesses.length === 0 && (
              <div className="col-span-full text-center py-8 text-[#808080]">
                <p>No businesses yet. Create businesses first.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Business Access Tab */}
        <TabsContent value="access" className="mt-6">
          <h3 className="text-sm text-[#808080] uppercase tracking-widest mb-4">User Business Access</h3>
          <p className="text-xs text-[#B0B0B0] mb-4">Assign businesses to users. Super Admins have access to all businesses by default.</p>
          <div className="bg-[#121212] border border-[rgba(255,255,255,0.12)] rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[rgba(255,255,255,0.12)] hover:bg-transparent">
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Name</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Role</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest">Assigned Businesses</TableHead>
                  <TableHead className="text-[#B0B0B0] text-xs uppercase tracking-widest w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.user_id} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-[#1A1A1A]">
                    <TableCell className="text-white font-medium">{u.name || u.email}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${ROLE_COLORS[u.role] || ROLE_COLORS.agent}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.role === 'super_admin' ? (
                        <span className="text-xs text-[#FFB800]">All (Super Admin)</span>
                      ) : (u.business_ids || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(u.business_ids || []).map(bid => {
                            const biz = businesses.find(b => b.business_id === bid);
                            return biz ? (
                              <span key={bid} className="text-xs px-2 py-0.5 rounded bg-[#007AFF]/10 text-[#007AFF]">{biz.name}</span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-[#808080]">No access</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.role !== 'super_admin' && hasRole('super_admin') && (
                        <button onClick={() => openBusinessAccess(u)}
                          className="p-1.5 rounded hover:bg-[#1A1A1A] text-[#B0B0B0] hover:text-[#00FF99] transition-colors duration-200"
                          data-testid={`edit-access-${u.user_id}`} title="Manage business access">
                          <Pencil size={14} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingRole} onOpenChange={(v) => { if (!v) setEditingRole(null); }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="bg-transparent border-[rgba(255,255,255,0.12)] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                {ROLES.map(r => (
                  <SelectItem key={r} value={r} className="text-white hover:bg-[#1A1A1A]">{ROLE_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleUpdateRole} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Update Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(v) => { if (!v) setResetPasswordUser(null); }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#B0B0B0]">
              Set a new password for <strong className="text-white">{resetPasswordUser?.name || resetPasswordUser?.email}</strong>
            </p>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">New Password</Label>
              <Input data-testid="admin-reset-pw-input" value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password"
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Min 12 characters" />
            </div>
            <Button onClick={handleAdminResetPassword} data-testid="admin-reset-pw-submit"
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Reset Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Own Password Dialog */}
      <Dialog open={changingOwnPassword} onOpenChange={setChangingOwnPassword}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Change Your Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Current Password</Label>
              <Input data-testid="own-current-pw" value={ownPasswordForm.current} onChange={e => setOwnPasswordForm(p => ({...p, current: e.target.value}))} type="password"
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Current password" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">New Password</Label>
              <Input data-testid="own-new-pw" value={ownPasswordForm.newPw} onChange={e => setOwnPasswordForm(p => ({...p, newPw: e.target.value}))} type="password"
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Min 12 characters" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Confirm New Password</Label>
              <Input data-testid="own-confirm-pw" value={ownPasswordForm.confirm} onChange={e => setOwnPasswordForm(p => ({...p, confirm: e.target.value}))} type="password"
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Repeat new password" />
            </div>
            <Button onClick={handleChangeOwnPassword} data-testid="own-change-pw-submit"
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Change Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={creatingUser} onOpenChange={setCreatingUser}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Full Name</Label>
              <Input data-testid="new-user-name" value={newUserForm.name} onChange={e => setNewUserForm(p => ({ ...p, name: e.target.value }))}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Email</Label>
              <Input data-testid="new-user-email" type="email" value={newUserForm.email} onChange={e => setNewUserForm(p => ({ ...p, email: e.target.value }))}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="jane@company.com" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Temporary Password</Label>
              <Input data-testid="new-user-password" value={newUserForm.password} onChange={e => setNewUserForm(p => ({ ...p, password: e.target.value }))}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="Min 12 characters" />
              <p className="text-xs text-[#808080] mt-1">They can change it after their first login.</p>
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Role</Label>
              <Select value={newUserForm.role} onValueChange={(v) => setNewUserForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.12)]">
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r} className="text-white hover:bg-[#1A1A1A]">{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {newUserForm.role !== 'super_admin' && (
              <div>
                <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Business Access</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto mt-1">
                  {businesses.map(b => (
                    <label key={b.business_id} className="flex items-center gap-3 p-2 rounded hover:bg-[#1A1A1A] cursor-pointer">
                      <input type="checkbox" checked={newUserForm.business_ids.includes(b.business_id)}
                        onChange={() => toggleNewUserBusiness(b.business_id)}
                        className="w-4 h-4 rounded border-[rgba(255,255,255,0.12)] accent-[#00FF99]" />
                      <span className="text-sm text-white">{b.name}</span>
                    </label>
                  ))}
                  {businesses.length === 0 && <p className="text-xs text-[#808080]">No businesses yet.</p>}
                </div>
              </div>
            )}
            <Button onClick={handleCreateUser} data-testid="create-user-submit"
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Resend Config Dialog */}
      <Dialog open={!!editingResend} onOpenChange={(v) => { if (!v) setEditingResend(null); }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Configure Resend API</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Resend API Key</Label>
              <Input data-testid="admin-resend-key" value={resendForm.resend_api_key} onChange={e => setResendForm(p => ({ ...p, resend_api_key: e.target.value }))}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1 font-mono text-sm" placeholder="re_..." type="password" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0] uppercase tracking-widest">Sender Email</Label>
              <Input data-testid="admin-sender-email" value={resendForm.sender_email} onChange={e => setResendForm(p => ({ ...p, sender_email: e.target.value }))}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white mt-1" placeholder="noreply@yourdomain.com" />
            </div>
            <p className="text-xs text-[#808080]">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[#00FF99] hover:underline">resend.com/api-keys</a></p>
            <Button onClick={handleUpdateResend} data-testid="save-resend-config" className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Save Configuration
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Business Access Dialog */}
      <Dialog open={!!editingBusinessAccess} onOpenChange={(v) => { if (!v) setEditingBusinessAccess(null); }}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Manage Business Access</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-[#B0B0B0]">
              Select businesses for <strong className="text-white">{editingBusinessAccess?.name || editingBusinessAccess?.email}</strong>
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {businesses.map(b => (
                <label key={b.business_id} className="flex items-center gap-3 p-2 rounded hover:bg-[#1A1A1A] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBusinessIds.includes(b.business_id)}
                    onChange={() => toggleBusiness(b.business_id)}
                    className="w-4 h-4 rounded border-[rgba(255,255,255,0.12)] accent-[#00FF99]"
                    data-testid={`access-checkbox-${b.business_id}`}
                  />
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${b.color || '#00FF99'}20` }}>
                      <Building2 size={14} style={{ color: b.color || '#00FF99' }} />
                    </div>
                    <span className="text-sm text-white">{b.name}</span>
                    <span className="text-xs text-[#808080]">({b.type})</span>
                  </div>
                </label>
              ))}
              {businesses.length === 0 && (
                <p className="text-sm text-[#808080] text-center py-4">No businesses available</p>
              )}
            </div>
            <Button onClick={handleSaveBusinessAccess} data-testid="save-business-access"
              className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">
              Save Access
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
